import SectionDivider from 'components/shared/SectionDivider';
import Typography from 'components/Typography';
import { FormattedMessage } from 'react-intl';
import { ReactNode } from 'react';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import ProResponse from 'types/ProResponse';
import { validatePaymentForm } from 'utils/PaymentFormHelper';
import CreditCardForm from 'components/forms/CreditCardForm';
import ApplePayButton from './ApplePayButton';
import ErrorText from './ErrorText';
import * as styles from './PaymentForm.module.scss';
import PayPalButton from './PayPalButton';

type Props = {
  coupon: Coupon;
  currencyCode: CurrencyCode;
  cvv: string;
  cvvError: ReactNode;
  errors: ProResponse['errors'];
  expiration: string;
  expirationError: ReactNode;
  name: string;
  nameError: ReactNode;
  number: string;
  numberError: ReactNode;
  plan: Plan;
  postalCode: string;
  setName: (name: string) => void;
  setNumber: (number: string) => void;
  setPostalCode: (postalCode: string) => void;
  userId: number;
  setCvv: (cvv: string) => void;
  setNumberError: (error: ReactNode) => void;
  setExpiration: (expiration: string) => void;
  setNameError: (error: ReactNode) => void;
  postalCodeError: ReactNode;
  setPostalCodeError: (error: ReactNode) => void;
  setErrors: (errors: ProResponse['errors']) => void;
  setCvvError: (error: ReactNode) => void;
  recurlyPublicKey: string;
  setExpirationError: (error: ReactNode) => void;
};

export default function PaymentForm({
  coupon,
  currencyCode,
  plan,
  recurlyPublicKey,
  userId,
  errors,
  setErrors,
  expiration,
  number,
  cvv,
  postalCode,
  name,
  setExpiration,
  setNumber,
  setCvv,
  setPostalCode,
  setName,
  setNumberError,
  numberError,
  nameError,
  setNameError,
  setCvvError,
  cvvError,
  postalCodeError,
  setPostalCodeError,
  expirationError,
  setExpirationError
}: Props) {
  function handleCreditCardFormChange(event: any) {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, {});

    switch (name) {
      case 'number':
        setNumber(value);
        break;
      case 'expiration':
        setExpiration(value);
        break;
      case 'cvv':
        setCvv(value);
        setCvvError(payload.errors.cvv);
        break;
      case 'postalCode':
        setPostalCode(value);
        setPostalCodeError(payload.errors.postalCode);
        break;
      case 'name':
        setName(value);
        setNameError(payload.errors.name);
        break;
      default:
        throw new Error(`Missing credit card form name ${name}`);
    }
  }

  return (
    <>
      <ApplePayButton
        coupon={coupon}
        currencyCode={currencyCode}
        plan={plan}
        recurlyPublicKey={recurlyPublicKey}
        setErrors={setErrors}
        userId={userId}
      />
      <PayPalButton
        coupon={coupon}
        currencyCode={currencyCode}
        plan={plan}
        recurlyPublicKey={recurlyPublicKey}
        setErrors={setErrors}
        userId={userId}
      />
      <SectionDivider classes={styles.divider}>
        <Typography variant="caption">
          <FormattedMessage defaultMessage="Or enter payment details" />
        </Typography>
      </SectionDivider>
      <CreditCardForm
        changeHandler={handleCreditCardFormChange}
        cvv={cvv}
        errors={{ expiration: expirationError, name: nameError, number: numberError, cvv: cvvError, postalCode: postalCodeError }}
        expiration={expiration}
        isCouponInputExpanded={false}
        isCouponInputShown={false}
        name={name}
        number={number}
        postalCode={postalCode}
        setError={(name, error) => {
          // these are then only two errors set on the credit card form
          switch (name) {
            case 'expiration':
              setExpirationError(error);
              break;
            case 'number':
              setNumberError(error);
              break;
            default:
              throw new Error(`missing credit card form error handling for name '${name}'`);
          }
        }}
        // toggleCouponCode={null}
      />
      {errors && <ErrorText errors={errors} />}
    </>
  );
}
